import { ConditionalRendering, Tabs, Text } from "@marketplace/ui";
import { getRouteApi } from "@tanstack/react-router";
import { useAtomValue } from "jotai";
import { useResetAtom } from "jotai/utils";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Pagination } from "@/components";
import { TransactionsEmptyState } from "@/modules/shared";

import { integrationStatusModalAtom } from "../../atoms/integration-status-atom";
import { orderDetailModalAtom } from "../../atoms/order-detail-atom";
import { payoutStatusModalAtom } from "../../atoms/payout-status-atom";
import { sendAccessModalAtom } from "../../atoms/send-access-atom";
import { ExportOrderModal } from "../../components/export-order-modal";
import { IntegrationStatusModal } from "../../components/integration-status-modal";
import { OrderDetailModal } from "../../components/order-detail-modal";
import { OrderList } from "../../components/order-list";
import { OrderListFilter } from "../../components/order-list-filter";
import { PayoutStatusModal } from "../../components/payout-status-modal";
import { SendAccessModal } from "../../components/send-access-modal";
import { ORDER_LIST_PER_PAGE_LIMIT } from "../../constants";
import { useGetOrderCountQuery } from "../../hooks/queries/use-get-order-count-query";
import { useGetOrderListQuery } from "../../hooks/queries/use-get-order-list-query";
import classes from "./order-list-page.module.css";

const route = getRouteApi("/orders/");

type TabTitleProps = {
  value: string;
  title: string;
  count: number | undefined;
};

const TabTitle = ({ value, title, count }: TabTitleProps) => {
  return (
    <Tabs.Tab value={value} className="p-0 h-fit">
      <Text type="heading" className="text-inherit px-[14px]">
        {title} ({count ?? 0})
      </Text>
      <div className="w-full mt-[14px] h-1 bg-transparent rounded-lg" />
    </Tabs.Tab>
  );
};

export const OrderListPage = () => {
  const { status, page, q, start_date, end_date } = route.useSearch();
  const navigate = route.useNavigate();
  const { t } = useTranslation("order", { keyPrefix: "orderListPage" });
  const integrationStatusModal = useAtomValue(integrationStatusModalAtom);
  const orderDetailModal = useAtomValue(orderDetailModalAtom);
  const payoutStatusModal = useAtomValue(payoutStatusModalAtom);
  const sendAccessModal = useAtomValue(sendAccessModalAtom);
  const resetIntegrationStatusModal = useResetAtom(integrationStatusModalAtom);
  const resetOrderDetailModal = useResetAtom(orderDetailModalAtom);
  const resetPayoutStatusModal = useResetAtom(payoutStatusModalAtom);
  const resetSendAccessModal = useResetAtom(sendAccessModalAtom);

  const handleTabsChange = (value: string | null) => {
    return navigate({
      search: (prev) => ({
        ...prev,
        page: 1,
        status: (value as typeof status) ?? "all"
      })
    });
  };

  const handlePageChange = (value: number) => {
    return navigate({
      search: (prev) => ({
        ...prev,
        page: value
      })
    });
  };

  const { data, isLoading } = useGetOrderListQuery({
    queryParams: {
      q,
      page,
      limit: ORDER_LIST_PER_PAGE_LIMIT,
      order_status: status,
      start_date,
      end_date
    }
  });

  const { data: countData } = useGetOrderCountQuery({
    queryParams: {
      q,
      start_date,
      end_date
    }
  });

  const orderListData = data?.data ?? [];
  const orderCountData = countData?.data;
  const metadata = data?.metadata;

  const emptyStateLabel = useMemo(() => {
    switch (status) {
      case "all":
        return t("emptyStateLabel.default");
      case "waiting_for_payment":
        return t("emptyStateLabel.waitingForPayment");
      case "paid":
        return t("emptyStateLabel.paid");
      case "done":
        return t("emptyStateLabel.done");
      case "failed":
        return t("emptyStateLabel.failed");
      default:
        return t("emptyStateLabel.default");
    }
  }, [t, status]);

  return (
    <>
      <IntegrationStatusModal
        opened={integrationStatusModal.visible}
        onClose={resetIntegrationStatusModal}
        orderId={integrationStatusModal.orderId}
      />

      <PayoutStatusModal
        opened={payoutStatusModal.visible}
        onClose={resetPayoutStatusModal}
        orderId={payoutStatusModal.orderId}
      />

      <OrderDetailModal
        opened={orderDetailModal.visible}
        onClose={resetOrderDetailModal}
      />

      <SendAccessModal
        opened={sendAccessModal.visible}
        onClose={resetSendAccessModal}
      />

      <ExportOrderModal />

      <div className="mb-6">
        <Text type="heading" size="l" className="text-humble-blue-500">
          {t("title")}
        </Text>
        <Text className="text-fjord-grey-400">{t("description")}</Text>
      </div>

      <OrderListFilter />

      <section>
        <Tabs
          variant="unstyled"
          value={status}
          onChange={handleTabsChange}
          classNames={classes}
        >
          <Tabs.List className="gap-x-[26px]">
            <TabTitle
              value="all"
              title={t("statusTab.all")}
              count={orderCountData?.total_orders}
            />
            <TabTitle
              value="order_created"
              title={t("statusTab.orderCreated")}
              count={orderCountData?.order_created_count}
            />
            <TabTitle
              value="waiting_for_payment"
              title={t("statusTab.waitingForPayment")}
              count={orderCountData?.order_waiting_for_payment_count}
            />
            <TabTitle
              value="paid"
              title={t("statusTab.paid")}
              count={orderCountData?.order_paid_count}
            />
            <TabTitle
              value="done"
              title={t("statusTab.done")}
              count={orderCountData?.order_done_count}
            />
            <TabTitle
              value="failed"
              title={t("statusTab.failed")}
              count={orderCountData?.order_failed_count}
            />
          </Tabs.List>

          {Array.from([
            "all",
            "order_created",
            "waiting_for_payment",
            "paid",
            "done",
            "failed"
          ]).map((orderStatus) => (
            <ConditionalRendering
              condition={orderStatus === status}
              key={orderStatus}
            >
              <Tabs.Panel key={status} value={status}>
                <ConditionalRendering
                  condition={!isLoading && !orderListData.length}
                >
                  <div className="relative mt-[42px] flex justify-center">
                    <TransactionsEmptyState />
                    <Text
                      type="heading"
                      className="absolute bottom-[65px] text-fjord-grey-500"
                    >
                      {emptyStateLabel}
                    </Text>
                  </div>
                </ConditionalRendering>

                <OrderList
                  orderListData={orderListData}
                  isLoading={isLoading}
                />
              </Tabs.Panel>
            </ConditionalRendering>
          ))}
        </Tabs>

        <ConditionalRendering condition={orderListData.length}>
          <Pagination
            currentPageTotal={orderListData.length}
            totalData={metadata?.total}
            total={metadata?.totalPage ?? 0}
            value={metadata?.page}
            onChange={handlePageChange}
          />
        </ConditionalRendering>
      </section>
    </>
  );
};
